/**
 * Fine-art border gate (iteration-1 feedback: "info box before they continue").
 * Fine-art prints default UNDECIDED; before Continue we force a choice — white
 * margin, handling border (width from the pricing borders config), or
 * print-to-edge — per print, with a bulk
 * "set all" shortcut. C-Type prints never appear here (they default to edge).
 */

import { parseSizeId, type BorderMode, type PricingData, type ResolvedSize } from '@rpl/pricing';
import { useStore } from '../../store/store';
import { borderInfo, borderModeLabel, borderOptionLabel, fineArtBorderGate, sizeShort, sizeWithUnit } from '../../lib/sizePaper';
import { ModalShell } from './ModalShell';
import { Segmented } from './Segmented';
import type { OrderActions } from './useOrderActions';

const FA_MODES: BorderMode[] = ['none', 'white', 'handling'];

export interface BorderGateModalProps {
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  actions: OrderActions;
  onClose: () => void;
  onContinue: () => void;
}

export function BorderGateModal({ pricing, catalog, actions, onClose, onContinue }: BorderGateModalProps) {
  const prints = useStore((s) => s.prints);
  const photos = useStore((s) => s.photos);
  const undecided = fineArtBorderGate(prints);

  const proceed = () => {
    onClose();
    onContinue();
  };

  return (
    <ModalShell onClose={onClose} width={640} label="Choose a fine-art border" className="bgate">
      <div className="bgate__head">
        <div>
          <div className="bgate__title">Choose a border for your fine-art prints</div>
          <div className="bgate__sub">Borders don’t change the price. They just change how we finish the paper.</div>
        </div>
        <button type="button" className="bgate__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <ul className="bgate__info">
        {FA_MODES.map((m) => (
          <li key={m} className="bgate__info-row">
            <span className="bgate__info-name">{borderModeLabel(m)}</span>
            <span className="bgate__info-text">
              {borderInfo(pricing, undecided[0]?.sizeId ?? 'fa-16x20', m)}
            </span>
          </li>
        ))}
      </ul>

      <div className="bgate__bulk">
        <span className="bgate__bulk-label">Set all to:</span>
        <button type="button" className="pill-btn" onClick={() => actions.decideFineArtBorders('none')}>
          Print to edge
        </button>
        <button type="button" className="pill-btn" onClick={() => actions.decideFineArtBorders('white')}>
          White border
        </button>
        <button type="button" className="pill-btn" onClick={() => actions.decideFineArtBorders('handling')}>
          Handling border
        </button>
      </div>

      <div className="bgate__rows">
        {undecided.length === 0 ? (
          <div className="bgate__done">✓ All set. You’re good to continue.</div>
        ) : (
          undecided.map((p) => {
            const photo = photos.find((ph) => ph.id === p.photoId);
            void catalog;
            return (
              <div key={p.id} className="bgate__row">
                <div className="bgate__row-info">
                  <span className="bgate__row-file">{photo?.filename ?? 'photo'}</span>
                  <span className="bgate__row-size">{sizeWithUnit(parseSizeId(p.sizeId).size)} fine art</span>
                </div>
                <Segmented
                  variant="light"
                  ariaLabel={`Border for ${photo?.filename ?? 'photo'}`}
                  segments={FA_MODES.map((m) => ({ key: m, label: borderOptionLabel(pricing, p.sizeId, m) }))}
                  value={null}
                  onChange={(key) => actions.setBorder(p.id, key as BorderMode)}
                />
              </div>
            );
          })
        )}
      </div>

      <div className="bgate__foot">
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Back
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={undecided.length > 0}
          onClick={proceed}
        >
          Continue →
        </button>
      </div>
    </ModalShell>
  );
}
